import { attr, belongsTo } from "@ember-data/model";
import { service } from "@ember/service";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type Vaccine from "parog-web/models/vaccine";
import type { SemeiaMedicSession } from "parog-web/services/session";

type CytomegalovirusStatusStringEnum =
  | "d_plus_r_plus"
  | "d_plus_r_minus"
  | "d_minus_r_plus"
  | "d_minus_r_minus";

type MrcStageStringEnum =
  | "stage-undefined"
  | "stage-1"
  | "stage-2"
  | "stage-3"
  | "stage-3-a"
  | "stage-3-b"
  | "stage-4"
  | "stage-5"
  | "hemodialysis"
  | "peritoneal-dialysis";

type HemodialysisType = "at-home" | "in-udm" | "in-self-dialysis-unit";

type TransplantedOrganStringEnum =
  | "bipulmonary"
  | "kidney"
  | "kidney-pancreas"
  | "kidney-liver"
  | "kidney-lung"
  | "kidney-lung-heart"
  | "kidney-heart-liver"
  | "liver-lung"
  | "lung-kidney"
  | "lung-heart"
  | "monopulmonary";

type AbmClassificationStringEnum =
  | "total_incapacity"
  | "third_party_need"
  | "autonomous_walk";

type DonorCreatinineStringEnum = "unknown" | "lte_190" | "gt_190";

type HtaStringEnum = "treated" | "yes" | "no" | "unknown";
type comorbiditiesStringEnum = "true" | "false" | "unknown";
type DoSportStringEnum = "true" | "false" | "stoped";
type FamilyIcBackgroundEnum = "true" | "false" | "under_55";

type HormoneReceptor = {
  percentage: number;
  intensity: "low" | "medium" | "high";
};

type TnmClassification = {
  t: string;
  n: string;
  m: string;
};

type StageStringEnum = "1" | "2" | "3" | "4" | "5";

type GleasonScoreStringEnum =
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type HistologyStringEnum = "CIC" | "CCI" | "CLI";

type Her2StringEnum = "positive" | "negative";

type OncogeneticConsultationStringEnum = "yes" | "no" | "isExpected";

type PelvicSonogramStringEnum = "normal" | "abnormal" | "not-applicable";

type HematoCancerTypeStringEnum = "leukemia" | "myeloma" | "lymphoma";

type PancreaticCancerTypeStringEnum =
  | "adenocarcinoma"
  | "ampullome"
  | "neuroendocrine-tumour";

type Child = {
  gender: string;
  birthyear: Date;
  city: string;
};

type MainDiagnosisStringEnum =
  | "bipolarDisorderType1"
  | "bipolarDisorderType2"
  | "cyclothimicDisorder"
  | "lightDepression"
  | "mediumDepression"
  | "persistingDepression";

type MaritalStatus = "single" | "couple" | "separated" | "widowed";

type ProfessionalActivity =
  | "unemployed"
  | "activeFullTime"
  | "activePartTime"
  | "retired"
  | "student"
  | "volonteer";

type Housing =
  | "single"
  | "parentsPlace"
  | "ownFamilyHome"
  | "childrenHome"
  | "anotherFamilyMember"
  | "houseShare"
  | "community";

type PrTypeStringEnum = "seropositive" | "seronegative" | "unknown";

type BoneErosionStringEnum = "erosive" | "non-erosive" | "unknown";

type ProstateCancerTypeEnum = "adenocarcinoma";

type nonSmallCellCancerTypeEnum =
  | "adenocarcinoma"
  | "squamous-cell-carcinoma"
  | "large-cell-carcinoma";

export default class MedicalHistoryChangedJournalEvent extends JournalEvent {
  static type = "medical-history-changed-journal-event";

  @belongsTo("patient", {
    async: false,
    inverse: "medicalHistoryChangedJournalEvent"
  })
  declare patient: Patient;

  @attr("vaccines")
  declare vaccines: Vaccine[];

  @attr("string")
  declare comments: string;

  @attr("date") declare diagnosisDate: Date;

  // Nephrowise specific
  @attr("date") declare transplantDate: Date;
  @attr("string") declare transplantRank: string;
  @attr("string") declare nephropatie: string;
  @attr("string") declare underlyingDisease: string;
  @attr("string")
  declare cytomegalovirusStatus: CytomegalovirusStatusStringEnum;
  @attr("string") declare donorType: string;
  @attr("boolean", { allowNull: true }) declare immunologicalRisk: boolean;
  @attr("boolean", { allowNull: true }) declare incompatibleAbo: boolean;
  @attr("string") declare mrcStage: MrcStageStringEnum;
  @attr("string") declare hemodialysisType: HemodialysisType;
  @attr("boolean", { allowNull: true })
  declare mrcPreexistingTransplant: boolean;
  @attr("string") declare transplantedOrgan: TransplantedOrganStringEnum;
  @attr("string") declare abmClassification: AbmClassificationStringEnum;
  @attr("number") declare creatinineThreeMonthsAfterTransplant: number;
  @attr("string") declare donorCreatinine: DonorCreatinineStringEnum;
  @attr("number") declare rejectsFirstYear: number;
  @attr("string") declare heartFailureType: string;
  @attr("string") declare nyhaStage: string;
  @attr("date") declare diagnosticDate: Date;
  @attr("number") declare fevg: number;
  @attr("number") declare state: number;
  @attr("number") declare height: number;
  @attr("number") declare weight: number;
  @attr("number") declare year: number;
  @attr("number") declare hba: number;
  @attr("date") declare hbaDate: Date;
  @attr("string") declare mrc: comorbiditiesStringEnum;
  @attr("number") declare dfg: number;
  @attr("number") declare rac: number;
  @attr("string") declare reti: comorbiditiesStringEnum;
  @attr("string") declare neur: comorbiditiesStringEnum;
  @attr("string") declare mcva: comorbiditiesStringEnum;
  @attr("string") declare ic: comorbiditiesStringEnum;
  @attr("string") declare oth: comorbiditiesStringEnum;
  @attr("boolean") declare exp: boolean;
  @attr("string") declare hta: HtaStringEnum;
  @attr("number") declare systolic: number;
  @attr("number") declare diastolic: number;
  @attr("boolean") declare met: boolean;
  @attr("boolean") declare isglt2: boolean;
  @attr("boolean") declare arglp1: BooleanConstructor;
  @attr("boolean") declare dyslipidemieTreated: boolean;
  @attr("number") declare lastDyslipidemie: number;
  @attr("boolean") declare smoker: number;
  @attr("string") declare doSport: DoSportStringEnum;
  @attr("boolean") declare insulin: boolean;
  @attr("boolean") declare idpp4: boolean;
  @attr("string") declare familyIcBackground: FamilyIcBackgroundEnum;
  @attr("number") declare albuminuria: number;
  @attr("boolean") declare isFollowingDiabetologist: boolean;
  @attr("string") declare registrationTransplantWaitingList: string;
  @attr("string") declare assessmentRealisation: string;
  @attr("string") declare assessmentResult: string;
  @attr("string") declare professionalActivity: ProfessionalActivity;
  @attr("string") declare housing: Housing;
  @attr("string") declare lifestyle: string;
  @attr("string") declare diseaseActivity: string;
  @attr("string") declare diagnosticType: string;
  // Oncowise specific
  @service declare session: SemeiaMedicSession;

  @attr("string") declare side: "left" | "right";
  @attr("string") declare stage: StageStringEnum;
  @attr("string") declare histology: HistologyStringEnum;
  @attr("string") declare her2: Her2StringEnum;
  @attr("boolean", { allowNull: true }) declare fertilityPreservation: boolean;
  @attr("string")
  declare oncogeneticConsultation: OncogeneticConsultationStringEnum;
  @attr("string") declare tnmComment: string;
  @attr("string") declare pelvicSonogram: PelvicSonogramStringEnum;
  @attr("number") declare ki67: number;
  @attr("string") declare hematoCancerType: HematoCancerTypeStringEnum;
  @attr() declare tnmClassification: TnmClassification;
  @attr() declare estrogenReceptor: HormoneReceptor;
  @attr() declare progesteroneReceptor: HormoneReceptor;
  @attr("string") declare maritalStatus: MaritalStatus;
  @attr("boolean", { allowNull: true }) declare hasChildren: boolean;
  @attr("children", {
    defaultValue() {
      return [];
    }
  })
  declare children: Child[];
  @attr("string") declare job: string;
  @attr("boolean", { allowNull: true }) declare relapse: boolean;
  @attr("boolean", { allowNull: true }) declare tripleNegative: boolean;
  @attr("boolean", { allowNull: true })
  declare familyHistoryBreastCancer: boolean;
  @attr() declare familyHistoryBreastCancerMember: string[];
  @attr("boolean", { allowNull: true })
  declare familyHistoryOvarianCancer: boolean;
  @attr() declare familyHistoryOvarianCancerMember: string[];
  @attr("boolean", { allowNull: true }) declare brca1: boolean;
  @attr() declare brca1Member: string[];
  @attr("boolean", { allowNull: true }) declare brca2: boolean;
  @attr() declare brca2Member: string[];
  @attr("boolean", { allowNull: true }) declare otherCancer: boolean;
  @attr() declare otherCancerType: string[];
  @attr("boolean", { allowNull: true }) declare menopause: boolean;
  @attr("boolean", { allowNull: true }) declare contraception: boolean;
  @attr("string", { allowNull: true }) declare cellCancerType: string;
  @attr("string", { allowNull: true })
  declare nonSmallCellCancerType: nonSmallCellCancerTypeEnum;
  @attr() declare mutation: string[];
  @attr("boolean", { allowNull: true }) declare statutPDL1: boolean;
  @attr("boolean", { allowNull: true }) declare smokingPatients: boolean;
  @attr("boolean", { allowNull: true }) declare lungCancerFamily: boolean;
  @attr("boolean", { allowNull: true })
  declare chronicBronchopneumopathy: boolean;
  @attr("string") declare pancreaticCancerType: PancreaticCancerTypeStringEnum;
  @attr() declare pancreaticMutation: string[];
  @attr("boolean", { allowNull: true })
  declare familialPancreaticCancer: boolean;
  @attr("boolean", { allowNull: true })
  declare hereditaryChronicPrss1GeneMutation: boolean;
  @attr("boolean", { allowNull: true })
  declare familyHistoryProstateCancer: boolean;
  @attr() declare familyHistoryProstateCancerMember: string[];
  @attr("string") declare prostateCancerType: ProstateCancerTypeEnum;
  @attr() declare prostateMutations: string[];
  @attr("boolean", { allowNull: true }) declare hormoneSensitiveCancer: boolean;
  @attr("string") declare gleasonScore: GleasonScoreStringEnum;

  // Mentalwise specific
  @attr("number") declare numberOfTs: number;
  @attr("string") declare mainDiagnosis: MainDiagnosisStringEnum;
  @attr("boolean") declare suicideRisk: boolean;
  @attr("date") declare suicidalThoughtsStartDate: Date;

  // Rhumatowise specific
  @attr("string") declare prType: PrTypeStringEnum;
  @attr("string") declare boneErosion: BoneErosionStringEnum;
  @attr("date") declare erosionDiagnosisDate: Date;
  @attr("string") declare facteurRhumatoidesValue: string;
  @attr("date") declare facteurRhumatoidesDate: Date;
  @attr("string") declare antiCppValue: string;
  @attr("date") declare antiCppDate: Date;
  @attr("string") declare anticorpsAntiNucleairesValue: string;
  @attr("date") declare anticorpsAntiNucleairesDate: Date;
  @attr("string") declare serologieHivValue: string;
  @attr("date") declare serologieHivDate: Date;
  @attr("string") declare antigeneHbsValue: string;
  @attr("date") declare antigeneHbsDate: Date;
  @attr("string") declare anticorpsAntiHbsValue: string;
  @attr("date") declare anticorpsAntiHbsDate: Date;
  @attr("string") declare anticorpsAntiHbcValue: string;
  @attr("date") declare anticorpsAntiHbcDate: Date;
  @attr("string") declare serologieHepatiteCValue: string;
  @attr("date") declare serologieHepatiteCDate: Date;
  @attr("string") declare electropheresValue: string;
  @attr("date") declare electropheresDate: Date;
  @attr("string") declare quantiferonValue: string;
  @attr("date") declare quantiferonDate: Date;

  get hormoneReceptors(): "positive" | "negative" | undefined {
    if (!this.estrogenReceptor || !this.progesteroneReceptor) {
      return undefined;
    }

    const estrogenReceptor = this.estrogenReceptor;
    const progesteroneReceptor = this.progesteroneReceptor;

    function isEstrogenPositive() {
      return estrogenReceptor && estrogenReceptor.percentage > 0;
    }
    function isProgesteronePositive() {
      return progesteroneReceptor && progesteroneReceptor.percentage > 0;
    }
    function isEstrogenNegative() {
      return estrogenReceptor && estrogenReceptor.percentage == 0;
    }
    function isProgesteroneNegative() {
      return progesteroneReceptor && progesteroneReceptor.percentage == 0;
    }

    // If at least on receptor > 0 then positive
    if (isEstrogenPositive() || isProgesteronePositive()) {
      return "positive";
    }
    // If the two receptors === 0 then negative
    else if (isEstrogenNegative() && isProgesteroneNegative()) {
      return "negative";
    }
    return undefined;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "medical-history-changed-journal-event": MedicalHistoryChangedJournalEvent;
  }
}
